import {
  type createApiClient,
  type EncryptedSystemEventPayload,
  type PublicGroupInvite,
  type SerializedWrappedKey,
  type SystemEvent,
} from '@adieuu/shared';
import {
  encryptGroupName,
  encryptMemberSettings,
  encryptSystemEvent,
  type MemberSettingsMap,
} from './conversationCryptoService';

type ApiClient = ReturnType<typeof createApiClient>;

export async function addMemberAction(
  api: ApiClient,
  conversationId: string,
  identityId: string,
  encryptedSystemEvent?: EncryptedSystemEventPayload
): Promise<{ ok: boolean; errorCode?: string; message?: string }> {
  try {
    const resp = await api.conversations.addMember(conversationId, identityId, encryptedSystemEvent);
    if (resp.success) return { ok: true };
    return { ok: false, errorCode: resp.error?.code, message: resp.error?.message };
  } catch {
    return { ok: false };
  }
}

export async function listPendingGroupInvitesAction(
  api: ApiClient,
  conversationId: string
): Promise<PublicGroupInvite[]> {
  try {
    const resp = await api.conversations.listPendingInvitesForConversation(conversationId);
    if (!resp.success || !resp.data) return [];
    return resp.data.invites;
  } catch {
    return [];
  }
}

export async function revokeGroupInviteAction(
  api: ApiClient,
  conversationId: string,
  inviteId: string
): Promise<boolean> {
  try {
    const resp = await api.conversations.revokeGroupInvite(conversationId, inviteId);
    return resp.success;
  } catch {
    return false;
  }
}

export async function removeMemberAction(
  api: ApiClient,
  conversationId: string,
  identityId: string,
  encryptedSystemEvent?: EncryptedSystemEventPayload
): Promise<boolean> {
  try {
    const resp = await api.conversations.removeMember(conversationId, identityId, encryptedSystemEvent);
    return resp.success;
  } catch {
    return false;
  }
}

export async function leaveGroupAction(
  api: ApiClient,
  conversationId: string,
  options?: {
    transferAdminTo?: string;
    transferStrategy?: 'oldest' | 'most_active';
    encryptedSystemEvent?: string;
    systemEventNonce?: string;
  }
): Promise<boolean> {
  try {
    const resp = await api.conversations.leave(conversationId, options);
    return resp.success;
  } catch {
    return false;
  }
}

export async function promoteToAdminAction(
  api: ApiClient,
  conversationId: string,
  identityId: string,
  encryptedSystemEvent?: EncryptedSystemEventPayload
): Promise<boolean> {
  try {
    const resp = await api.conversations.promoteToAdmin(conversationId, identityId, encryptedSystemEvent);
    return resp.success;
  } catch {
    return false;
  }
}

export async function terminateGroupAction(
  api: ApiClient,
  conversationId: string
): Promise<boolean> {
  try {
    const resp = await api.conversations.terminateGroup(conversationId);
    return resp.success;
  } catch {
    return false;
  }
}

export async function renameGroupAction(
  api: ApiClient,
  conversationId: string,
  newName: string,
  metadataKey: Uint8Array,
  wrappedMetadataKeys: SerializedWrappedKey[],
  systemEvent?: SystemEvent
): Promise<{ ok: boolean; encryptedName?: string; nameNonce?: string; wrappedMetadataKeys?: SerializedWrappedKey[] }> {
  try {
    const encrypted = encryptGroupName(newName, conversationId, metadataKey);
    const encryptedSystemEvent = systemEvent
      ? encryptSystemEvent(systemEvent, conversationId, metadataKey)
      : undefined;
    const resp = await api.conversations.updateName(
      conversationId,
      encrypted.encryptedName,
      encrypted.nameNonce,
      wrappedMetadataKeys,
      encryptedSystemEvent
    );
    if (!resp.success) return { ok: false };
    return {
      ok: true,
      encryptedName: encrypted.encryptedName,
      nameNonce: encrypted.nameNonce,
      wrappedMetadataKeys,
    };
  } catch {
    return { ok: false };
  }
}

export async function updateMemberSettingsAction(
  api: ApiClient,
  conversationId: string,
  settings: MemberSettingsMap,
  metadataKey: Uint8Array,
  wrappedMetadataKeys: SerializedWrappedKey[]
): Promise<{
  ok: boolean;
  encryptedMemberSettings?: string;
  memberSettingsNonce?: string;
  wrappedMetadataKeys?: SerializedWrappedKey[];
}> {
  try {
    const encrypted = encryptMemberSettings(settings, conversationId, metadataKey);
    const resp = await api.conversations.updateMemberSettings(
      conversationId,
      encrypted.encryptedMemberSettings,
      encrypted.memberSettingsNonce,
      wrappedMetadataKeys
    );
    if (!resp.success) return { ok: false };
    return {
      ok: true,
      encryptedMemberSettings: encrypted.encryptedMemberSettings,
      memberSettingsNonce: encrypted.memberSettingsNonce,
      wrappedMetadataKeys,
    };
  } catch {
    return { ok: false };
  }
}
